import React, { useEffect, useMemo, useState } from 'react';
import Container from '../components/Container';
import MemberLevelCards, { MemberLevelCardData } from '../components/Soul/MemberLevelCards';
import SmallTag from '../components/SmallTag';
import Subtitle from '../components/Subtitle';
import SoulRankingTable from '../components/Soul/SoulRankingTable';
import { getSoulLevels, getSoulRanking } from '../utils/axios';
import TopSection from '../components/Soul/TopSection';
import SoulPointsPanel from '../components/Soul/SoulPointsPanel';
import { readContractOnce, multiReadContract } from '../contracts/utils/readContract';
import { soulContract, soulDetailContract, kogeContract, govBNBContract, nftContract } from '../contracts/contracts';
import { useAccount, useBalance } from 'wagmi';
import { useTranslation } from 'react-i18next';
import BigNumber from 'bignumber.js';

interface SoulLevel {
  level: string;
  min: number;
  max: number | null;
}

const LEVEL_STYLE: Record<string, {
  tagColor: string;
  tagBg: string;
  icon: string;
  iconClass: string;
  labelKey: string;
  benefitKeys: string[];
  detailsLink?: string;
}> = {
  Member: {
    tagColor: '#171412',
    tagBg: '#D6D3D1',
    icon: '/soul/icon-soul.svg',
    iconClass: 'w-[88px] h-[88px]',
    labelKey: 'soul_level_member',
    benefitKeys: [],
  },
  Entry: {
    tagColor: '#171412',
    tagBg: '#FFD166',
    icon: '/level-basic.svg',
    iconClass: '',
    labelKey: 'soul_level_entry',
    benefitKeys: ['soul_member_basic_benefit_gas_free'],
    detailsLink: 'https://docs.48.club/48-soul-point',
  },
  Gold: {
    tagColor: '#171412',
    tagBg: '#F670C7',
    icon: '/level-gold.svg',
    iconClass: '',
    labelKey: 'soul_gold_member',
    benefitKeys: ['soul_member_gold_benefit_entry_level', 'soul_member_gold_benefit_airdrops'],
  },
  Platinum: {
    tagColor: '#171412',
    tagBg: '#2ED3B7',
    icon: '/level-platinum.svg',
    iconClass: '',
    labelKey: 'soul_platinum_member',
    benefitKeys: [
      'soul_member_platinum_benefit_exclusive_chat',
      'soul_member_platinum_benefit_domain_email',
      'soul_member_platinum_benefit_free_vpn',
    ],
  },
};

const formatLevelPoints = (min: number) => `${min}+`;

const SoulPoint: React.FC = () => {
  const { address } = useAccount();
  // const address = '0xa31A940ecFB4Cb9fE7884eB3C9a959Db79CbdC70';
  const { t } = useTranslation('soul');
  const result = useBalance({
    address: address,
  })
  const [rankData, setRankData] = useState<any[]>([]);
  const [soulLevels, setSoulLevels] = useState<SoulLevel[]>([]);
  const [taskPoints, setTaskPoints] = useState<any>({})
  const [pointData, setPointData] = useState<any>({ bnb: result?.data?.value || 0 })
  const [isMember, setIsMember] = useState(false);
  const [accountData, setAccountData] = useState<any>({});
  const handleFetchSoulPoint = async (address: string) => {
    const res = await getSoulRanking({address, detail: true})
    if (res.status === 200 && res.data.code === 200) {
      setPointData(res.data.data);
      setAccountData({
        address: address,
        points: res.data.data.points || 0,
      });
    }
    console.log(res, 'res');
  }
  const fetchSoul = async () => {
    const isSoulMember = await readContractOnce({
      ...soulContract,
      functionName: 'isMember',
      args: [address],
    });
    const soulDetail = await multiReadContract([{
      ...soulDetailContract,
      functionName: 'getBscStakePoint',
      args: [address],
    }, {
      ...soulDetailContract,
      functionName: 'getGovBNBPoint',
      args: [address],
    }, {
      ...soulDetailContract,
      functionName: 'getKogePoint',
      args: [address],
    }, {
      ...soulDetailContract,
      functionName: 'getNftPoint',
      args: [address],
    }, {
      ...soulDetailContract,
      functionName: 'getStakePoint',
      args: [address],
    },
    {
      ...kogeContract,
      functionName: 'balanceOf',
      args: [address],
    },
    {
      ...nftContract,
      functionName: 'balanceOf',
      args: [address],
    },
    {
      ...govBNBContract,
      functionName: 'balanceOf',
      args: [address],
    },
  ],);
    console.log(isSoulMember, 'isSoulMember');
    console.log(soulDetail, 'soulDetail');
    if (soulDetail && Array.isArray(soulDetail)) {
      const [
        bscStakePoint,
        govBNBPoint,
        kogePoint,
        nftPoint,
        stakePoint, koge, nft, govBNB
      ] = soulDetail;
      
      // 将 BigInt 转换为数字，处理可能的 undefined 值
      const points = {
        bscStakePoint: Number(bscStakePoint.result || 0),
        govBNBPoint: Number(govBNBPoint.result || 0),
        kogePoint: Number(kogePoint.result || 0),
        nftPoint: Number(nftPoint.result || 0),
        stakePoint: Number(stakePoint.result || 0),
        koge: new BigNumber(String(koge.result || 0)).dividedBy(10 ** 18).toNumber().toFixed(2),
        nft: Number(nft.result || 0),
        govBNB: new BigNumber(String(govBNB.result || 0)).dividedBy(10 ** 18).toNumber(),
      };
      setTaskPoints((prev: any) => ({ ...prev, ...points }));
      console.log(points, 'points');
    }
    if (isSoulMember) {
      setIsMember(true);
    } else {
      setIsMember(false);
    }
  }
  useEffect(() => {
    if (address) {
      fetchSoul();
      handleFetchSoulPoint(address);
    } else {
      setIsMember(false);
    }
  }, [address]);
  const mintCallback = () => {
    if (address) {
      fetchSoul();
      handleFetchSoulPoint(address);
    }
  }
  console.log(pointData, 'pointData', taskPoints);
  useEffect(() => {
    getSoulLevels().then((res: any) => {
      if (res.status === 200 && res.data.code === 200 && Array.isArray(res.data.data)) {
        const levels = [...res.data.data]
          .sort((a: SoulLevel, b: SoulLevel) => a.min - b.min)
          .slice(1);
        setSoulLevels(levels);
      }
    });
  }, []);
  useEffect(() => {
    getSoulRanking({}).then((res: any) => {
      if (res.status === 200 && res.data.code === 200) {
        // 过滤数据并添加rank字段
        const filteredData = res.data.data.filter((item: any) => item.points >= 1);
        const dataWithRank = filteredData.map((item: any, index: number) => ({
          ...item,
          rank: index + 1
        }));
        setRankData(dataWithRank);
      }
    });
  }, []);
  const memberLevels: MemberLevelCardData[] = useMemo(() => {
    return soulLevels.map((level) => {
      const style = LEVEL_STYLE[level.level] || {
        tagColor: '#171412',
        tagBg: '#A8A29E',
        icon: '/soul/icon-soul.svg',
        iconClass: 'w-[88px] h-[88px]',
        labelKey: '',
        benefitKeys: [] as string[],
      };
      return {
        tag: style.labelKey ? t(style.labelKey) : level.level,
        tagColor: style.tagColor,
        tagBg: style.tagBg,
        points: formatLevelPoints(level.min),
        icon: <img src={style.icon} alt={level.level} className={style.iconClass} />,
        benefits: style.benefitKeys.map((key) => ({ text: t(key) })),
        detailsLink: style.detailsLink,
      };
    });
  }, [soulLevels, t]);
  const userRankData = useMemo(() => {
    const data = rankData.find((item: any) => item.address.toLowerCase() === address?.toLowerCase());
    if (data) {
      return {
        ...accountData,
        rank: data.rank,
      }
    }
    return {
      ...accountData,
      rank: 0,
    };
  }, [rankData, address, accountData]);
  console.log(isMember, 'isMember');
  return (
    <div className="bg-gray-950 text-white relative" >
      <div className="absolute top-0 left-0 w-full min-h-[400px] sm:min-h-[530px] opacity-50" style={{ backgroundImage: 'url(/soul/soul-bg.png)', backgroundSize: 'cover', backgroundPosition: 'top' }}></div>
      <TopSection isMember={isMember} />
      {address && (
        <SoulPointsPanel taskPoints={taskPoints} pointData={pointData} isMember={isMember} mintCallback={mintCallback} />
      )}
      <Container className="py-[20px] sm:py-[40px] mx-[12px] !w-auto relative">
        <div className="flex items-center justify-start gap-[8px] mb-[12px]">
          <SmallTag>01.</SmallTag>
          <SmallTag>{t('soul_page_how_it_works')}</SmallTag>
        </div>
        <Subtitle>{t('soul_page_membership_tiers')}</Subtitle>
        <MemberLevelCards data={memberLevels} className="mt-10" />
      </Container>
      <Container className="py-[20px] sm:py-[40px] mx-[12px] !w-auto">
        <div className="flex items-center justify-start gap-[8px] mb-[12px]">
          <SmallTag>02.</SmallTag>
          <SmallTag>{t('soul_page_what_is_your_ranking')}</SmallTag>
        </div>
        <Subtitle>{t('soul_page_soul_points_ranking')}</Subtitle>
        <SoulRankingTable data={rankData} className="mt-10" accountData={userRankData} />
      </Container>
    </div>
  );
};

export default SoulPoint; 